import { BOTTOM_DRAWER_CONTENT_ID } from "testIds";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import Title from "common/Typography/Title";
import React, { CSSProperties, ReactNode, useEffect, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import Drawer from "common/Drawers/Drawer";
import { useVirtualKeyboard } from "utils/hooks/misc";
import useBackButtonHandler from "utils/hooks/useBackButtonHandler.ts";
import CloseOutlined from "@ant-design/icons/CloseOutlined";

interface MobileFiltersDrawerProps {
  title?: string;
  triggerElement?: React.JSX.Element;
  content: ReactNode;
  rightButtons?: React.JSX.Element;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  contentStyle?: CSSProperties;
  shouldCloseDrawer?: boolean;
  setShouldCloseDrawer?: (shouldClose: boolean) => void;
  className?: string;
  footer?: ReactNode;
  showClose?: boolean;
  zIndex?: number;
}

const BottomDrawer = ({
  title,
  triggerElement,
  content,
  rightButtons,
  open: propsOpen,
  onOpenChange,
  onClose: propsOnClose,
  contentStyle,
  shouldCloseDrawer,
  setShouldCloseDrawer,
  className,
  footer,
  showClose,
  zIndex,
}: MobileFiltersDrawerProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { isNewLook } = useTheme();
  // New look: a close button whenever the header has no actions of its own.
  const closeShown = showClose ?? (isNewLook && !rightButtons);
  const { keyboardHeight } = useVirtualKeyboard();
  const [isOpen, setOpen] = useState(false);
  const { registerBackButtonListener } = useBackButtonHandler();
  const open = propsOpen ?? isOpen;
  const setIsOpen = onOpenChange ?? setOpen;

  const closeDrawer = () => {
    propsOnClose?.();
    setIsOpen(false);
  };

  useEffect(() => {
    if (open) {
      return registerBackButtonListener(() => {
        closeDrawer();
        return true;
      });
    }
  }, [open]);

  useEffect(() => {
    if (shouldCloseDrawer && isOpen) {
      closeDrawer();
      setShouldCloseDrawer?.(false);
    }
  }, [shouldCloseDrawer]);

  return (
    <>
      {!!triggerElement &&
        React.cloneElement(triggerElement, {
          onClick: (e) => {
            e.stopPropagation();
            setIsOpen(true);
          },
        })}
      <StyledDrawer
        placement="bottom"
        open={open}
        onClose={(e) => {
          e.stopPropagation();
          closeDrawer();
        }}
        size="auto"
        // The sheet draws its own header (title, actions, optional close);
        // antd 6 renders its close button outside the hidden class.
        closable={false}
        $keyboardHeight={keyboardHeight}
        className={className}
        zIndex={zIndex}
      >
        {title?.length && (
          <TitleWrapper>
            <Title level={3}>{title}</Title>
            {rightButtons}
            {closeShown && (
              <CloseButton
                type="button"
                onClick={closeDrawer}
                aria-label={t("buttons.close")}
              >
                <CloseOutlined />
              </CloseButton>
            )}
          </TitleWrapper>
        )}
        <ContentWrapper
          data-testid={BOTTOM_DRAWER_CONTENT_ID}
          style={contentStyle}
        >
          {content}
        </ContentWrapper>
        {!!footer && <FooterWrapper>{footer}</FooterWrapper>}
      </StyledDrawer>
    </>
  );
};

export default BottomDrawer;

// antd 6 puts this class on .ant-drawer-section, inside the content wrapper,
// so the sheet is capped here: the title row stays pinned and ContentWrapper
// scrolls instead of the sheet growing off the top of the screen.
const StyledDrawer = styled(Drawer)<{ $keyboardHeight?: number }>`
  & {
    max-height: min(500px, calc(100dvh - 24px));
    overflow: hidden;
    border-radius: 12px 12px 0 0;
    ${(p) =>
      p.$keyboardHeight &&
      `
      padding-bottom: ${p.$keyboardHeight}px;
    `};
  }

  .ant-drawer-body {
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0 !important;
    overflow: hidden !important;
  }

  .ant-drawer-close {
    display: none;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* New look: the sheet uses the theme's panel radius. */
      & {
        border-radius: ${p.theme.radii.card}px ${p.theme.radii.card}px 0 0;
      }
    `}

  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;
const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  gap: 12px;
  flex-shrink: 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* Title left in 16/600 ink over a hairline, like a modal's. */
      min-height: 56px;
      padding: 12px 12px 12px 20px;
      border-bottom-color: ${getNewLookTokens(p.theme).hairline};
      h3.ant-typography {
        margin: 0;
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;
        color: ${getNewLookTokens(p.theme).ink};
      }
    `}
`;
const ContentWrapper = styled.div`
  padding: 20px;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
`;
const FooterWrapper = styled.div`
  padding: 16px 20px;
  flex-shrink: 0;
  position: relative;
  bottom: 0;
  width: 100%;
  background: ${(p) => p.theme.colors.layoutBgColor};
`;
const CloseButton = styled.button`
  display: grid;
  place-items: center;
  min-width: 40px;
  min-height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  color: ${(p) => p.theme.colors.textColor};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      min-width: 32px;
      min-height: 32px;
      border-radius: ${p.theme.radii.control}px;
      color: ${getNewLookTokens(p.theme).secondary};
    `}
`;
